import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { styles } from "./style";
import { RootState } from "redux/store";
import { bindActionCreators, Dispatch } from "redux";
import * as Router from "react-router-dom";
import { connect } from "react-redux";
import { resetTasksPageFilters, setTasksPageFilters } from "redux/slice/tasks-page"
import { getAgents, getUserUnsafe, getTasksPageFilters, getLeadsWithTasks } from "redux/selector";
import { TaskStatus } from "type/task-status";
import { TaskType } from "type/task-type";
import { agentTasksDev } from "shared/env";

const mapStateToProps = (state: RootState) => {
	const filters = getTasksPageFilters(state);
	const agents = getAgents(state);
	const user = getUserUnsafe(state);
	const leads = getLeadsWithTasks(state).sort((a, b) =>
		a.label < b.label ? -1 : 1
	);
	return {
		user,
		filters,
		leads,
		agents
	};
}

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	setTasksPageFilters,
	resetTasksPageFilters,
}, dispatch);

interface Props extends 
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	Router.RouteComponentProps,
	WithStyles<typeof styles>
{ 
	open: boolean;
	onClose: Function;
	count: number;
}

interface State { 
}

const initialState: State = {
}

class Component extends React.Component<Props, State> {

	public constructor(props: Props) {
		super(props);
		this.state = initialState;
	}

	public render() {
		const {
			user,
			classes = {},
			open,
			onClose,
			count,
			filters,
			agents,
			leads,
			setTasksPageFilters,
			resetTasksPageFilters
		} = this.props;
		if (!user) {
			throw new Error();
		}
		return (
			<>
				<Mui.Dialog
					open={open}
					onClose={() => onClose()}
					scroll="paper"
					fullWidth={true}
					maxWidth="sm"
					classes={{ paper: classes.dialogPaper }}
				>
					<div className={classes.dialogTitle}>
						<span>Filters</span>
						<Mui.IconButton onClick={() => onClose()} className={classes.closeButton}>
							<Icons.X size={24} />
						</Mui.IconButton>
					</div>
					<Mui.DialogContent className={classes.dialogContentRoot}>
						<div className={classes.sectionsContainer}>
							{/* Status Section */}
							<div>
								<Mui.Typography className={classes.sectionTitle}>Status</Mui.Typography>
								<div className={classes.checkboxGroup}>
									{TaskStatus.values()
										.filter(status => status.id !== TaskStatus.DELETED.id)
										.map(status => (
											<div key={status.id} className={classes.checkboxItem}>
												<Mui.Checkbox
													className={classes.checkbox}
													checked={filters.status?.includes(status.id) || false}
													onChange={() => {
														const currentStatus = filters.status || [];
														const newStatus = currentStatus.includes(status.id)
															? currentStatus.filter(s => s !== status.id)
															: [...currentStatus, status.id];
														setTasksPageFilters({
															...filters,
															status: newStatus,
														});
													}}
												/>
												<Mui.Typography className={classes.checkboxLabel}>
													{status.name}
												</Mui.Typography>
											</div>
										))}
								</div>
							</div>

							{/* Task Type Section */}
							{user.permissions.crmTags && (
								<div>
									<Mui.Typography className={classes.sectionTitle}>Task Type</Mui.Typography>
									<div className={classes.checkboxGroup}>
										{TaskType.values().map(type => (
											<div key={type.id} className={classes.checkboxItem}>
												<Mui.Checkbox
													className={classes.checkbox}
													checked={filters.type?.includes(type.id) || false}
													onChange={() => {
														const currentType = filters.type || [];
														const newType = currentType.includes(type.id)
															? currentType.filter(t => t !== type.id)
															: [...currentType, type.id];
														setTasksPageFilters({
															...filters,
															type: newType,
														});
													}}
												/>
												<type.icon size={14} />
												<Mui.Typography className={classes.checkboxLabel}>
													{type.name}
												</Mui.Typography>
											</div>
										))}
									</div>
								</div>
							)}

							{/* Other Section */}
							<div>
								<Mui.Typography className={classes.sectionTitle}>Other</Mui.Typography>
								<div className={classes.otherSection}>
									{user.admin && agentTasksDev && (
										<Mui.TextField
											select
											fullWidth
											variant="outlined"
											label="Agent"
											SelectProps={{
												multiple: true,
												value: filters.agent || [],
												onChange: (e) => {
													setTasksPageFilters({
														...filters,
														agent: e.target.value as number[],
													});
												},
											}}
											className={classes.selectField}
										>
											{agents.map((agent) => (
												<Mui.MenuItem key={agent.id} value={agent.id}>
													{agent.name}
												</Mui.MenuItem>
											))}
										</Mui.TextField>
									)}

									<Mui.TextField
										select
										fullWidth
										variant="outlined"
										label="Lead"
										value={filters.lead || ""}
										onChange={(e) => {
											setTasksPageFilters({
												...filters,
												lead: e.target.value ? parseInt(e.target.value) : undefined,
											});
										}}
										className={classes.selectField}
									>
										<Mui.MenuItem value="">
											<em>None</em>
										</Mui.MenuItem>
										{leads.map((lead) => (
											<Mui.MenuItem key={lead.id} value={lead.id}>
												{lead.label}
											</Mui.MenuItem>
										))}
									</Mui.TextField>

									<div>
										<Mui.Typography className={classes.sectionTitle}>Priority</Mui.Typography>
										<div className={classes.checkboxGroup}>
											<div className={classes.checkboxItem}>
												<Mui.Checkbox
													className={classes.checkbox}
													checked={filters.priority?.includes(true) || false}
													onChange={() => {
														const currentPriority = filters.priority || [];
														const newPriority = currentPriority.includes(true)
															? currentPriority.filter(p => p !== true)
															: [...currentPriority, true];
														setTasksPageFilters({
															...filters,
															priority: newPriority.length > 0 ? newPriority : undefined,
														});
													}}
												/>
												<Mui.Typography className={classes.checkboxLabel}>
													Priority
												</Mui.Typography>
											</div>
											<div className={classes.checkboxItem}>
												<Mui.Checkbox
													className={classes.checkbox}
													checked={filters.priority?.includes(false) || false}
													onChange={() => {
														const currentPriority = filters.priority || [];
														const newPriority = currentPriority.includes(false)
															? currentPriority.filter(p => p !== false)
															: [...currentPriority, false];
														setTasksPageFilters({
															...filters,
															priority: newPriority.length > 0 ? newPriority : undefined,
														});
													}}
												/>
												<Mui.Typography className={classes.checkboxLabel}>
													Not Priority
												</Mui.Typography>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</Mui.DialogContent>
					<Mui.DialogActions className={classes.dialogActions}>
						<Mui.Button
							className={classes.clearButton}
							variant="outlined"
							color="primary"
							onClick={() => {
								resetTasksPageFilters();
								onClose();
							}}
						>
							Clear All
						</Mui.Button>
						<Mui.Button
							className={classes.viewButton}
							variant="contained"
							color="primary"
							disabled={count < 1}
							onClick={() => {
								onClose();
							}}
						>
							View {count} Result{count === 1 ? "" : "s"}
						</Mui.Button>
					</Mui.DialogActions>
				</Mui.Dialog>
			</>
		);
	}
}

export const FilterDialog = withStyles(Router.withRouter(
	connect(mapStateToProps, mapDispatchToProps)(Component)
), styles);